import { Button, HStack } from '@sgme/ui';
import { FormattedMessage } from 'react-intl';

import { MarginValue } from '@/App/tiles/CashTile/shared/MarginValue.tsx';

import type { BidAskPair } from './types.ts';

/**
 * Props for MarginAmountDisplay component
 */
type MarginAmountDisplayProps = {
  /**
   * Margin amount (bid/ask)
   */
  amount: BidAskPair;

  /**
   * Amount currency
   */
  currency: string;

  /**
   * Callback called when toggling the currency
   */
  onCurrencyToggle: () => void;

  /**
   * Indicates if the amount currency is editable
   */
  isEditable: boolean;

  /**
   * Prefix for data-e2e attributes
   */
  dataE2ePrefix: string;
};

/**
 * Displays the commercial margin amount with currency
 * Allows toggling the currency via a button
 */
export function MarginAmountDisplay(props: MarginAmountDisplayProps) {
  const { amount, currency, onCurrencyToggle, isEditable, dataE2ePrefix } = props;

  const getMaximumFractionDigits = (value: number): number => {
    return value >= 1000 ? 1 : 0;
  };

  return (
    <>
      {/* Label */}
      <div className="fs-12 text-secondary fw-bold text-end">
        <FormattedMessage id="commercialMargin.amount" defaultMessage="Margin amount" />
      </div>

      {/* Amount (bid/ask) */}
      <div>
        <HStack
          horizontalAlignment="center"
          className="form-control form-control-sm readonly"
          data-e2e={`${dataE2ePrefix}-amount`}
        >
          <MarginValue
            value={amount.bid}
            minimumFractionDigits={0}
            maximumFractionDigits={getMaximumFractionDigits(amount.bid)}
            isEditable={isEditable}
            data-e2e={`${dataE2ePrefix}-amount-bid`}
          />

          <span>/</span>

          <MarginValue
            value={amount.ask}
            minimumFractionDigits={0}
            maximumFractionDigits={getMaximumFractionDigits(amount.ask)}
            isEditable={isEditable}
            data-e2e={`${dataE2ePrefix}-amount-ask`}
          />
        </HStack>
      </div>

      {/* Currency (toggle button) */}
      <div>
        <Button
          purpose="discreet"
          size="sm"
          className="w-100"
          onClick={onCurrencyToggle}
          disabled={!isEditable}
          data-e2e={`${dataE2ePrefix}-currency-toggle-button`}
        >
          {currency}
        </Button>
      </div>
    </>
  );
}
